import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  BarChart, Bar, LineChart, Line,
  XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, LabelList
} from 'recharts';
import Sidebar from '../components/Sidebar';
import { useAuth } from '../context/AuthContext';
import DemoBlockModal from '../components/DemoBlockModal';
import { isDemoUser } from '../utils/demoGuard';
import './ERDashboard.css';
import { BIGQUERY_CONFIG } from '../config';

import { API_BASE_URL } from '../config';

const PROJECT_ID = BIGQUERY_CONFIG.PROJECT;
const DATASET_NAME = BIGQUERY_CONFIG.DATASET;
const TABLE_NAME = 'HHA_PY';

// Formatter functions
const formatCurrency = (value) => `$${Number(value || 0).toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
const formatLOS = (value) => (Number(value || 0)).toFixed(1);
const formatRiskScore = (value) => (Number(value || 0)).toFixed(3);
const formatNumber = (value) => Number(value || 0).toLocaleString('en-US');
const formatPercent = (value) => (Number(value || 0)).toFixed(1) + '%';

function HHADashboardMain({ activeView, setActiveView }) {
  const { user, token, getAuthHeader } = useAuth();
  const [showDemoBlock, setShowDemoBlock] = React.useState(false);
  const [metrics, setMetrics] = useState({
    total_patients: 0,
    total_admits: 0,
    total_cost: 0,
    avg_los: 0,
    avg_risk_score: 0,
    cost_per_admit: 0,
    cost_per_day: 0,
    readmit_30d_pct: 0
  });
  const [data, setData] = useState({
    monthlyAdmits: [],
    monthlyCost: [],
    topFacilities: [],
    topDiagnosis: [],
    topCCSR: [],
    topDischargeLocation: [],
    topChronicConditions: [],
    byCohort: [],
    byRiskGroup: []
  });
  const [viewMode, setViewMode] = useState('admits'); // 'admits' or 'cost'
  const [beneficiaries, setBeneficiaries] = useState([]);
  const [filterOptions, setFilterOptions] = useState({
    'FAC_NAME': ['All'],
    'ADMITTING_OSCAR': ['All'],
    'ADMITTING_NPI': ['All'],
    'PRNCPL_DGNS_CD': ['All'],
    'YR_MO': ['All'],
    'DISCH_LOC': ['All'],
    'CCSR': ['All'],
    'READMIT_30D_FLAG': ['All']
  });
  const [filters, setFilters] = useState({
    'BENE_MBI_ID': ['All'],
    'PRNCPL_DGNS_CD': ['All'],
    'ADMITTING_OSCAR': ['All'],
    'ADMITTING_NPI': ['All'],
    'FAC_NAME': ['All'],
    'YR_MO': ['All'],
    'DISCH_LOC': ['All'],
    'CCSR': ['All'],
    'READMIT_30D_FLAG': ['All'],
    'CHRONIC': ['All'],
    'BENE_1ST_NAME': ['All'],
    'BENE_LAST_NAME': ['All'],
    'GENDER': ['All'],
    'COHORT': ['All']
  });
  const [loading, setLoading] = useState(true);



  useEffect(() => {
    fetchFilterOptions();
  }, []);

  useEffect(() => {
    fetchDashboardData();
  }, [filters]);

  const fetchFilterOptions = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/hha/filter-options`, {
        params: {
          project: PROJECT_ID,
          dataset: DATASET_NAME,
          table: TABLE_NAME
        }
      });
      
      if (response.data && response.data.filters) {
        const options = response.data.filters;
        setFilterOptions({
          'FAC_NAME': ['All', ...(options.fac_name || [])],
          'ADMITTING_OSCAR': ['All', ...(options.admitting_oscar || [])],
          'ADMITTING_NPI': ['All', ...(options.admitting_npi || [])],
          'PRNCPL_DGNS_CD': ['All', ...(options.prncpl_dgns_cd || [])],
          'YR_MO': ['All', ...(options.yr_mo || [])],
          'DISCH_LOC': ['All', ...(options.disch_loc || [])],
          'CCSR': ['All', ...(options.ccsr || [])],
          'READMIT_30D_FLAG': ['All', '0', '1']
        });
      }
    } catch (error) {
      console.error('Error fetching filter options:', error);
    }
  };

  const handleFilterChange = (field, values) => {
    setFilters(prev => ({ ...prev, [field]: values }));
  };

  const handleResetFilters = () => {
    setFilters({
      'GENDER': ['All'],
      'COHORT': ['All'],
      'CHRONIC': ['All'],
      'FAC_NAME': ['All'],
      'ADMITTING_OSCAR': ['All'],
      'ADMITTING_NPI': ['All'],
      'PRNCPL_DGNS_CD': ['All'],
      'YR_MO': ['All'],
      'DISCH_LOC': ['All'],
      'CCSR': ['All'],
      'READMIT_30D_FLAG': ['All'],
      'BENE_MBI_ID': ['All'],
      'BENE_1ST_NAME': ['All'],
      'BENE_LAST_NAME': ['All']
    });
  };

  const buildFilterParams = () => {
    let params = `project=${PROJECT_ID}&dataset=${DATASET_NAME}&table=${TABLE_NAME}`;
    
    Object.entries(filters).forEach(([field, values]) => {
      if (!values.includes('All') && values.length > 0) {
        const filterValue = values.join(',');
        params += `&${field.toLowerCase()}=${encodeURIComponent(filterValue)}`;
      }
    });
    
    return params;
  };

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      
      const params = buildFilterParams();
      
      const combinedRes = await axios.get(`${API_BASE_URL}/api/hha/combined?${params}`, { 
        timeout: 180000
      });
      
      const responseData = combinedRes.data.data;
      
      let riskScore = 0;
      try {
        const riskRes = await axios.get(`${API_BASE_URL}/api/hha/risk-score?${params}`, { 
          timeout: 60000
        });
        riskScore = riskRes.data.avg_risk_score || 0;
      } catch (riskError) {
        console.error('Error loading risk score:', riskError);
      }
      
      setMetrics({
        ...responseData.metrics,
        avg_risk_score: riskScore
      });
      
      try {
        const beneficiariesRes = await axios.get(`${API_BASE_URL}/api/hha/beneficiaries?${params}&limit=15`, { 
          timeout: 60000
        });
        setBeneficiaries(beneficiariesRes.data.beneficiaries);
      } catch (benefError) {
        console.error('Error loading beneficiaries:', benefError);
        setBeneficiaries([]);
      }
      
      const totalAdmits = responseData.metrics.total_admits;
      const totalCost = responseData.metrics.total_cost;
      
      const addPercentages = (dataArray) => {
        return dataArray.map(item => ({
          ...item,
          percentage: ((item.value / totalAdmits) * 100).toFixed(1),
          costPercentage: ((item.cost_value / totalCost) * 100).toFixed(1),
          costValue: item.cost_value || 0
        }));
      };
      
      setData({
        monthlyAdmits: responseData.monthly_admits || [],
        monthlyCost: responseData.monthly_cost || [],
        topFacilities: addPercentages(responseData.by_facility),
        topDiagnosis: addPercentages(responseData.by_diagnosis),
        topCCSR: addPercentages(responseData.by_ccsr || []),
        topDischargeLocation: addPercentages(responseData.by_discharge_location || []),
        topChronicConditions: addPercentages(responseData.by_chronic_condition || []),
        byCohort: addPercentages(responseData.by_cohort || []),
        byRiskGroup: addPercentages(responseData.by_risk_group || [])
      });
      
      setLoading(false);
    } catch (error) {
      console.error('Error fetching HHA data:', error);
      alert('Failed to load HHA data. Check console for details.');
      setLoading(false);
    }
  };

  const downloadCSV = async () => {
    if (isDemoUser(user)) { setShowDemoBlock(true); return; }
    try {
      const params = buildFilterParams();
      const response = await axios.get(`${API_BASE_URL}/api/hha/beneficiaries?${params}&limit=0`, {
        timeout: 180000
      });
      
      if (response.data && response.data.beneficiaries && response.data.beneficiaries.length > 0) {
        const allBeneficiaries = response.data.beneficiaries;
        const headers = Object.keys(allBeneficiaries[0]);
        const csvData = allBeneficiaries.map(row => 
          headers.map(header => row[header] || '')
        );
        
        const csvContent = [
          headers.join(','),
          ...csvData.map(row => row.map(cell => `"${cell}"`).join(','))
        ].join('\n');
        
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', 'hha_admissions.csv');
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    } catch (error) {
      console.error('Error downloading CSV:', error);
      alert('Failed to download CSV. Please try again.');
    }
  };

  if (loading) {
    return (
      <div className="page-with-sidebar">
        <Sidebar filters={filters} onFilterChange={handleFilterChange} onResetFilters={handleResetFilters} />
        <div className="dashboard" style={{padding: '0px'}}>
          <div className="metrics-row" style={{padding: '20px 15px', marginBottom: '20px'}}>
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <div key={i} className="metric-card" style={{
                animation: `pulse 1.5s ease-in-out infinite`,
                animationDelay: `${i * 0.1}s`,
                background: 'linear-gradient(90deg, #f3f4f6 25%, #e5e7eb 50%, #f3f4f6 75%)',
                backgroundSize: '200% 100%'
              }}>
                <div style={{height: '14px', width: '80px', backgroundColor: '#d1d5db', borderRadius: '4px', marginBottom: '8px'}}></div>
                <div style={{height: '28px', width: '100px', backgroundColor: '#d1d5db', borderRadius: '4px'}}></div>
              </div>
            ))}
          </div>
          <div style={{padding: '0px 15px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px'}}>
            {[1, 2].map((i) => (
              <div key={i} className="chart-card" style={{
                animation: `pulse 1.5s ease-in-out infinite`,
                animationDelay: `${(i + 6) * 0.1}s`,
                height: '350px',
                background: 'linear-gradient(90deg, #f3f4f6 25%, #e5e7eb 50%, #f3f4f6 75%)',
                backgroundSize: '200% 100%'
              }}></div>
            ))}
          </div>
          <style>{`
            @keyframes pulse {
              0%, 100% { opacity: 1; }
              50% { opacity: 0.5; }
            }
          `}</style>
        </div>
      </div>
    );
  }

  // Helper function to get chart data based on view mode
  const getChartData = (dataArray, useAdmits = true, skipSort = false) => {
    let processedData = [...dataArray];
    
    // Filter out items with 0 value
    processedData = processedData.filter(item => {
      const val = useAdmits ? item.value : item.costValue;
      return val > 0;
    });
    
    if (!skipSort) {
      processedData = processedData.sort((a, b) => {
        const aVal = useAdmits ? a.value : a.costValue;
        const bVal = useAdmits ? b.value : b.costValue;
        return bVal - aVal; // Descending order
      });
    }
    
    return processedData.map(item => ({
      name: item.name,
      value: useAdmits ? item.value : item.costValue,
      percentage: useAdmits ? item.percentage : item.costPercentage,
      admitValue: item.value,
      costValue: item.costValue
    }));
  };

  return (
    <div className="page-with-sidebar">
      {showDemoBlock && <DemoBlockModal onClose={() => setShowDemoBlock(false)} />}
      <Sidebar filters={filters} onFilterChange={handleFilterChange} onResetFilters={handleResetFilters} />
      <div className="dashboard" style={{padding: '0px'}}>
        <div style={{animation: 'fadeIn 0.5s ease-in-out'}}>
          <div className="metrics-row" style={{padding: '20px 15px', marginBottom: '20px'}}>
            <div className="metric-card">
              <div style={{display: 'flex', alignItems: 'center', gap: '10px'}}>
                <span style={{fontSize: '24px'}}>👥</span>
                <div>
                  <div className="metric-label">TOTAL PATIENTS</div>
                  <div className="metric-value">{formatNumber(metrics.total_patients)}</div>
                </div>
              </div>
            </div>
            <div className="metric-card">
              <div style={{display: 'flex', alignItems: 'center', gap: '10px'}}>
                <span style={{fontSize: '24px'}}>🏠</span>
                <div>
                  <div className="metric-label">HHA ADMITS</div>
                  <div className="metric-value">{formatNumber(metrics.total_admits)}</div>
                </div>
              </div>
            </div>
            <div className="metric-card">
              <div style={{display: 'flex', alignItems: 'center', gap: '10px'}}>
                <span style={{fontSize: '24px'}}>💰</span>
                <div>
                  <div className="metric-label">TOTAL COST</div>
                  <div className="metric-value">{formatCurrency(metrics.total_cost)}</div>
                </div>
              </div>
            </div>
            <div className="metric-card">
              <div style={{display: 'flex', alignItems: 'center', gap: '10px'}}>
                <span style={{fontSize: '24px'}}>⏱️</span>
                <div>
                  <div className="metric-label">AVG LOS</div>
                  <div className="metric-value">{formatLOS(metrics.avg_los)}</div>
                </div>
              </div>
            </div>
            <div className="metric-card">
              <div style={{display: 'flex', alignItems: 'center', gap: '10px'}}>
                <span style={{fontSize: '24px'}}>📊</span>
                <div>
                  <div className="metric-label">AVG RISK SCORE</div>
                  <div className="metric-value">{formatRiskScore(metrics.avg_risk_score)}</div>
                </div>
              </div>
            </div>
            <div className="metric-card">
              <div style={{display: 'flex', alignItems: 'center', gap: '10px'}}>
                <span style={{fontSize: '24px'}}>💵</span>
                <div>
                  <div className="metric-label">COST PER ADMIT</div>
                  <div className="metric-value">{formatCurrency(metrics.cost_per_admit)}</div>
                </div>
              </div>
            </div>
            <div className="metric-card">
              <div style={{display: 'flex', alignItems: 'center', gap: '10px'}}>
                <span style={{fontSize: '24px'}}>📅</span>
                <div>
                  <div className="metric-label">COST PER DAY</div>
                  <div className="metric-value">{formatCurrency(metrics.cost_per_day)}</div>
                </div>
              </div>
            </div>
            <div className="metric-card">
              <div style={{display: 'flex', alignItems: 'center', gap: '10px'}}>
                <span style={{fontSize: '24px'}}>🔄</span>
                <div>
                  <div className="metric-label">30 DAY READMIT %</div>
                  <div className="metric-value">{formatPercent(metrics.readmit_30d_pct)}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <style>{`
          @keyframes fadeIn {
            from {
              opacity: 0;
              transform: translateY(10px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }
        `}</style>

        {/* Monthly Line Charts */}
        <div style={{padding: '0px 15px', marginBottom: '20px'}}>
          <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px'}}>
            <div className="chart-card" style={{padding: '15px'}}>
              <h3 style={{margin: '0 0 15px 0'}}>Monthly HHA Admissions</h3>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={data.monthlyAdmits} margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="yr_mo" style={{fontSize: '12px'}} />
                  <YAxis style={{fontSize: '12px'}} />
                  <Tooltip 
                    formatter={(value) => formatNumber(value)}
                    cursor={{ fill: '#e0f2fe' }} contentStyle={{fontSize: '12px', backgroundColor: '#e0f2fe', border: '1px solid #0284c7', borderRadius: '4px'}}
                  />
                  <Line type="monotone" dataKey="admits" stroke="#0284c7" strokeWidth={2} dot={{ r: 4 }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
            
            <div className="chart-card" style={{padding: '15px'}}>
              <h3 style={{margin: '0 0 15px 0'}}>Monthly HHA Cost</h3>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={data.monthlyCost} margin={{ top: 5, right: 20, left: 60, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="yr_mo" style={{fontSize: '12px'}} />
                  <YAxis style={{fontSize: '12px'}} tickFormatter={formatCurrency} />
                  <Tooltip 
                    formatter={(value) => formatCurrency(value)}
                    cursor={{ fill: '#e0f2fe' }} contentStyle={{fontSize: '12px', backgroundColor: '#e0f2fe', border: '1px solid #0284c7', borderRadius: '4px'}}
                  />
                  <Line type="monotone" dataKey="cost" stroke="#16a34a" strokeWidth={2} dot={{ r: 4 }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        {/* Toggle for Admits/Cost */}
        <div style={{padding: '0px 15px', marginBottom: '20px'}}>
          <div style={{display: 'flex', gap: '10px', justifyContent: 'center'}}>
            <button
              onClick={() => setViewMode('admits')}
              style={{
                padding: '10px 24px',
                backgroundColor: viewMode === 'admits' ? '#0284c7' : '#e5e7eb',
                color: viewMode === 'admits' ? 'white' : '#1f2937',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '600',
                transition: 'all 0.3s ease'
              }}
            >
              Admits
            </button>
            <button
              onClick={() => setViewMode('cost')}
              style={{
                padding: '10px 24px',
                backgroundColor: viewMode === 'cost' ? '#0284c7' : '#e5e7eb',
                color: viewMode === 'cost' ? 'white' : '#1f2937',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '600',
                transition: 'all 0.3s ease'
              }}
            >
              Cost
            </button>
          </div>
        </div>

        <div className="charts-grid" style={{padding: '0px 15px', marginBottom: '30px'}}>
          <div className="chart-card" style={{padding: '15px 0px 15px 0px'}}>
            <h3 style={{margin: '0 0 15px 20px'}}>
              HHA {viewMode === 'admits' ? 'Admissions' : 'Cost'} by Top 15 Facilities
            </h3>
            <ResponsiveContainer width="100%" height={600}>
              <BarChart 
                data={getChartData(data.topFacilities, viewMode === 'admits')} 
                layout="vertical" 
                margin={{ top: 5, right: 20, left: 20, bottom: 5 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis type="number" tickFormatter={viewMode === 'admits' ? formatNumber : formatCurrency} />
                <YAxis type="category" dataKey="name" width={180} style={{fontSize: '12px'}} interval={0} tick={{dy: 4}} />
                <Tooltip cursor={{ fill: '#e0f2fe' }} content={({active, payload}) => {
                  if (active && payload && payload.length) {
                    const item = payload[0].payload;
                    return (
                      <div style={{backgroundColor: '#e0f2fe', border: '1px solid #0284c7', padding: '8px', borderRadius: '4px', fontSize: '12px'}}>
                        <p style={{margin: 0}}><strong>{item.name}</strong></p>
                        <p style={{margin: '4px 0 0 0'}}>
                          {viewMode === 'admits' 
                            ? `Admits: ${formatNumber(item.admitValue)} (${item.percentage}%)`
                            : `Cost: ${formatCurrency(item.costValue)} (${item.percentage}%)`
                          }
                        </p>
                      </div>
                    );
                  }
                  return null;
                }} />
                <Bar dataKey="value" fill="#0284c7">
                  <LabelList 
                    dataKey="value" 
                    position="insideRight" 
                    formatter={(value) => viewMode === 'admits' ? formatNumber(value) : formatCurrency(value)} 
                    style={{fontSize: '12px', fill: 'white'}} 
                  />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>

          <div className="chart-card" style={{padding: '15px 0px 15px 0px'}}>
            <h3 style={{margin: '0 0 15px 20px'}}>
              HHA {viewMode === 'admits' ? 'Admissions' : 'Cost'} by Top 15 Diagnoses
            </h3>
            <ResponsiveContainer width="100%" height={600}>
              <BarChart 
                data={getChartData(data.topDiagnosis, viewMode === 'admits')} 
                layout="vertical" 
                margin={{ top: 5, right: 20, left: 20, bottom: 5 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis type="number" tickFormatter={viewMode === 'admits' ? formatNumber : formatCurrency} />
                <YAxis type="category" dataKey="name" width={180} style={{fontSize: '12px'}} interval={0} tick={{dy: 4}} />
                <Tooltip cursor={{ fill: '#e0f2fe' }} content={({active, payload}) => {
                  if (active && payload && payload.length) {
                    const item = payload[0].payload;
                    return (
                      <div style={{backgroundColor: '#e0f2fe', border: '1px solid #0284c7', padding: '8px', borderRadius: '4px', fontSize: '12px'}}>
                        <p style={{margin: 0}}><strong>{item.name}</strong></p>
                        <p style={{margin: '4px 0 0 0'}}>
                          {viewMode === 'admits' 
                            ? `Admits: ${formatNumber(item.admitValue)} (${item.percentage}%)`
                            : `Cost: ${formatCurrency(item.costValue)} (${item.percentage}%)`
                          }
                        </p>
                      </div>
                    );
                  }
                  return null;
                }} />
                <Bar dataKey="value" fill="#0284c7">
                  <LabelList 
                    dataKey="value" 
                    position="insideRight" 
                    formatter={(value) => viewMode === 'admits' ? formatNumber(value) : formatCurrency(value)} 
                    style={{fontSize: '12px', fill: 'white'}} 
                  />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>

          <div className="chart-card" style={{padding: '15px 0px 15px 0px'}}>
            <h3 style={{margin: '0 0 15px 20px'}}>
              HHA {viewMode === 'admits' ? 'Admissions' : 'Cost'} by Top 15 CCSR
            </h3>
            <ResponsiveContainer width="100%" height={600}>
              <BarChart 
                data={getChartData(data.topCCSR, viewMode === 'admits')} 
                layout="vertical" 
                margin={{ top: 5, right: 20, left: 20, bottom: 5 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis type="number" tickFormatter={viewMode === 'admits' ? formatNumber : formatCurrency} />
                <YAxis type="category" dataKey="name" width={180} style={{fontSize: '12px'}} interval={0} tick={{dy: 4}} />
                <Tooltip cursor={{ fill: '#e0f2fe' }} content={({active, payload}) => {
                  if (active && payload && payload.length) {
                    const item = payload[0].payload;
                    return (
                      <div style={{backgroundColor: '#e0f2fe', border: '1px solid #0284c7', padding: '8px', borderRadius: '4px', fontSize: '12px'}}>
                        <p style={{margin: 0}}><strong>{item.name}</strong></p>
                        <p style={{margin: '4px 0 0 0'}}>
                          {viewMode === 'admits' 
                            ? `Admits: ${formatNumber(item.admitValue)} (${item.percentage}%)`
                            : `Cost: ${formatCurrency(item.costValue)} (${item.percentage}%)`
                          }
                        </p>
                      </div>
                    );
                  }
                  return null;
                }} />
                <Bar dataKey="value" fill="#0284c7">
                  <LabelList 
                    dataKey="value" 
                    position="insideRight" 
                    formatter={(value) => viewMode === 'admits' ? formatNumber(value) : formatCurrency(value)} 
                    style={{fontSize: '12px', fill: 'white'}} 
                  />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>

          <div className="chart-card" style={{padding: '15px 0px 15px 0px'}}>
            <h3 style={{margin: '0 0 15px 20px'}}>
              HHA {viewMode === 'admits' ? 'Admissions' : 'Cost'} by Top 15 Chronic Conditions
            </h3>
            <ResponsiveContainer width="100%" height={600}>
              <BarChart 
                data={getChartData(data.topChronicConditions, viewMode === 'admits')} 
                layout="vertical" 
                margin={{ top: 5, right: 20, left: 20, bottom: 5 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis type="number" tickFormatter={viewMode === 'admits' ? formatNumber : formatCurrency} />
                <YAxis type="category" dataKey="name" width={180} style={{fontSize: '12px'}} interval={0} tick={{dy: 4}} />
                <Tooltip cursor={{ fill: '#e0f2fe' }} content={({active, payload}) => {
                  if (active && payload && payload.length) {
                    const item = payload[0].payload;
                    return (
                      <div style={{backgroundColor: '#e0f2fe', border: '1px solid #0284c7', padding: '8px', borderRadius: '4px', fontSize: '12px'}}>
                        <p style={{margin: 0}}><strong>{item.name}</strong></p>
                        <p style={{margin: '4px 0 0 0'}}>
                          {viewMode === 'admits' 
                            ? `Admits: ${formatNumber(item.admitValue)} (${item.percentage}%)`
                            : `Cost: ${formatCurrency(item.costValue)} (${item.percentage}%)`
                          }
                        </p>
                      </div>
                    );
                  }
                  return null;
                }} />
                <Bar dataKey="value" fill="#0284c7">
                  <LabelList 
                    dataKey="value" 
                    position="insideRight" 
                    formatter={(value) => viewMode === 'admits' ? formatNumber(value) : formatCurrency(value)} 
                    style={{fontSize: '12px', fill: 'white'}} 
                  />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>

          <div className="chart-card" style={{padding: '15px 0px 15px 0px'}}>
            <h3 style={{margin: '0 0 15px 20px'}}>
              HHA {viewMode === 'admits' ? 'Admissions' : 'Cost'} by Cohort
            </h3>
            <ResponsiveContainer width="100%" height={400}>
              <BarChart 
                data={getChartData(data.byCohort, viewMode === 'admits')} 
                margin={{ top: 5, right: 30, left: 60, bottom: 5 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" style={{fontSize: '12px'}} />
                <YAxis tickFormatter={viewMode === 'admits' ? formatNumber : formatCurrency} />
                <Tooltip cursor={{ fill: '#e0f2fe' }} content={({active, payload}) => {
                  if (active && payload && payload.length) {
                    const item = payload[0].payload;
                    return (
                      <div style={{backgroundColor: '#e0f2fe', border: '1px solid #0284c7', padding: '8px', borderRadius: '4px', fontSize: '12px'}}>
                        <p style={{margin: 0}}><strong>{item.name}</strong></p>
                        <p style={{margin: '4px 0 0 0'}}>
                          {viewMode === 'admits' 
                            ? `Admits: ${formatNumber(item.admitValue)} (${item.percentage}%)`
                            : `Cost: ${formatCurrency(item.costValue)} (${item.percentage}%)`
                          }
                        </p>
                      </div>
                    );
                  }
                  return null;
                }} />
                <Bar dataKey="value" fill="#0284c7">
                  <LabelList 
                    dataKey="value" 
                    position="top" 
                    formatter={(value) => viewMode === 'admits' ? formatNumber(value) : formatCurrency(value)} 
                    style={{fontSize: '11px'}} 
                  />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>

          <div className="chart-card" style={{padding: '15px 0px 15px 0px'}}>
            <h3 style={{margin: '0 0 15px 20px'}}>
              HHA {viewMode === 'admits' ? 'Admissions' : 'Cost'} by Risk Score Grouping
            </h3>
            <ResponsiveContainer width="100%" height={400}>
              <BarChart 
                data={getChartData(data.byRiskGroup, viewMode === 'admits', true)} 
                margin={{ top: 5, right: 30, left: 60, bottom: 5 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" style={{fontSize: '12px'}} />
                <YAxis tickFormatter={viewMode === 'admits' ? formatNumber : formatCurrency} />
                <Tooltip cursor={{ fill: '#e0f2fe' }} content={({active, payload}) => {
                  if (active && payload && payload.length) {
                    const item = payload[0].payload;
                    return (
                      <div style={{backgroundColor: '#e0f2fe', border: '1px solid #0284c7', padding: '8px', borderRadius: '4px', fontSize: '12px'}}>
                        <p style={{margin: 0}}><strong>{item.name}</strong></p>
                        <p style={{margin: '4px 0 0 0'}}>
                          {viewMode === 'admits' 
                            ? `Admits: ${formatNumber(item.admitValue)} (${item.percentage}%)`
                            : `Cost: ${formatCurrency(item.costValue)} (${item.percentage}%)`
                          }
                        </p>
                      </div>
                    );
                  }
                  return null;
                }} />
                <Bar dataKey="value" fill="#0284c7">
                  <LabelList 
                    dataKey="value" 
                    position="top" 
                    formatter={(value) => viewMode === 'admits' ? formatNumber(value) : formatCurrency(value)} 
                    style={{fontSize: '11px'}} 
                  />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="table-section" style={{marginTop: '30px', marginLeft: '15px', marginRight: '15px', marginBottom: '20px'}}>
          <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px'}}>
            <h2 style={{margin: 0}}>HHA Admissions ({beneficiaries.length.toLocaleString()} records)</h2>
            <button 
              onClick={downloadCSV}
              style={{
                padding: '10px 20px',
                backgroundColor: '#0369a1',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '500'
              }}
              onMouseOver={(e) => e.target.style.backgroundColor = '#0284c7'}
              onMouseOut={(e) => e.target.style.backgroundColor = '#0369a1'}
            >
              Download CSV
            </button>
          </div>
          <div style={{overflowX: 'auto', backgroundColor: 'white', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)'}}>
            <table style={{width: '100%', borderCollapse: 'collapse'}}>
              <thead>
                <tr style={{backgroundColor: '#f9fafb', borderBottom: '2px solid #e5e7eb'}}>
                  {beneficiaries.length > 0 && Object.keys(beneficiaries[0]).map((column) => (
                    <th key={column} style={{padding: '12px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#374151', whiteSpace: 'nowrap'}}>
                      {column}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {beneficiaries.map((row, index) => (
                  <tr key={`${row.BENE_MBI_ID}-${row.ADMIT_ID || ''}-${index}`} style={{borderBottom: '1px solid #e5e7eb', backgroundColor: index % 2 === 0 ? 'white' : '#f9fafb'}}>
                    {Object.keys(row).map((column) => (
                      <td key={column} style={{padding: '12px', fontSize: '13px', color: '#1f2937', whiteSpace: 'nowrap'}}>
                        {row[column] !== null && row[column] !== undefined ? String(row[column]) : '-'}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

export default HHADashboardMain;
